/**
 * ImpersonationBanner - Persistent warning banner displayed when a Super Admin
 * is impersonating (viewing the platform as) another user.
 *
 * This component sits at the very top of the page (above the TopBar) to ensure
 * maximum visibility. It uses an amber/yellow color scheme to clearly indicate
 * that the current session is not the admin's real session.
 *
 * Displays:
 *	 - The name and roles of the user being impersonated
 *	 - The name of the Super Admin's own account
 *	 - A "Stop Impersonation" button to revert to the admin's real session
 *
 * The roles, and "Your own account", are there because the old wording
 * ("Viewing as Abbey E — Logged in as Super Admin") read as if the session's
 * role were Super Admin while viewing as an Admin: the seeded Super Admin
 * account is literally named "Super Admin".
 *
 * Only renders when `isImpersonating` is true in the AuthContext. When not
 * impersonating, this component returns null (renders nothing).
 *
 * Styling: Fixed at top, z-50 to stay above all other UI elements, amber bg
 * with dark text for high contrast visibility.
 */
import React from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { formatRoleLabel } from '../../../utils/formatLabel';

const ImpersonationBanner: React.FC<{ className?: string }> = ({ className }) => {
	// Pull impersonation state and the stop function from auth context
	const { isImpersonating, originalUser, user, stopImpersonation } = useAuth();

	// Only render the banner when actively impersonating another user
	if (!isImpersonating || !originalUser || !user) return null;

	const roles = (user.roles ?? []).map(formatRoleLabel).join(', ');

	return (
		<div className={`impersonation-banner fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-900 px-16px py-8px flex flex-wrap items-center justify-between gap-8px text-14px font-medium shadow-md ${className ?? ''}`} data-ref="organism-impersonation-banner">
			<span className="flex-1 min-w-0 text-13px tablet:text-14px">Viewing as <strong>{user.firstName} {user.lastName}</strong>{roles && ` (${roles})`}. Your own account: <strong>{originalUser.name}</strong></span>
			<button type="button" onClick={stopImpersonation} className="impersonation-banner__stop inline-flex items-center bg-amber-700 text-white px-12px py-4px min-h-44px desktop:min-h-32px rounded-md text-12px font-semibold hover:bg-amber-800 transition-colors">Stop Impersonation</button>
		</div>
	);
};

export default ImpersonationBanner;
